import { Grid, InputAdornment } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  FormContainer,
  FormInput,
  Information,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";

import { useCallback } from "react";
import {
  createSiteAppCheckIn,
  getSiteAppCheckInById,
  updateSiteAppCheckInById,
} from "../../../../../../services/siteAppCheckIn.service";
import {
  TSiteAppCheckInForm,
  siteAppCheckInSchema,
} from "./SiteAppCheckInSchema";

const translationPath = "page.settings.siteApp.siteAppCheckIn.";

const SiteAppCheckIn = () => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } =
    useFetch(createSiteAppCheckIn);

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateSiteAppCheckInById
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getSiteAppCheckInById
  );

  const handleSave = (v: TSiteAppCheckInForm) => {
    return save({
      ...v,
      time: Number(v.time),
      metersApproximation: Number(v.metersApproximation),
      insuranceAllowCheckin: v.insuranceAllowCheckin ?? undefined,
    });
  };

  const updateHandler = (v: TSiteAppCheckInForm, id: string) =>
    update({
      item: {
        ...v,
        id: v.id,
        time: Number(v.time),
        metersApproximation: Number(v.metersApproximation),
        insuranceAllowCheckin: v.insuranceAllowCheckin ?? undefined,
      },
      id: id,
    });

  const handleGet = useCallback(
    async (id: string): Promise<Treturn<TSiteAppCheckInForm>> => {
      const { data } = await get(id);
      if (data) {
        return {
          data: {
            ...data,
            time: data.time.toString(),
            metersApproximation: data.metersApproximation.toString(),
          },
          success: true,
        };
      }
      return {
        data: null,
        success: false,
      };
    },
    [get]
  );

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TSiteAppCheckInForm>
      saveFunction={handleSave}
      updateFunction={updateHandler}
      getFunction={handleGet}
      loading={loading}
      title={t(translationPath + "site_app_check_in")}
      schema={siteAppCheckInSchema}
      subtitle={t(translationPath + "new_site_app_check_in")}
      subtitleWatchField={"metersApproximation"}
      fields={() => (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Information info={t(translationPath + "help_meters")}>
              <FormInput
                size="small"
                name="metersApproximation"
                fullWidth
                label={
                  t(translationPath + "meters_approximation") ||
                  "Approximation in meters"
                }
                autoFocus
                type="number"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {t(translationPath + "meters")}
                    </InputAdornment>
                  ),
                }}
              />
            </Information>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Information info={t(translationPath + "help_time")}>
              <FormInput
                size="small"
                name="time"
                fullWidth
                label={t(translationPath + "time") || "Time"}
                type="number"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {t(translationPath + "min")}
                    </InputAdornment>
                  ),
                }}
              />
            </Information>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormInput
              size="small"
              name="sectorCode"
              fullWidth
              label={
                t(translationPath + "standard_service_sector_code") ||
                "Código Setor Atendimento Padrão"
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormInput
              size="small"
              name="latitude"
              fullWidth
              label={t(translationPath + "latitude") || "Latitude"}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormInput
              size="small"
              name="longitude"
              fullWidth
              label={t(translationPath + "longitude") || "Longitude"}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormInput
              size="small"
              name="unityCode"
              fullWidth
              label={
                t(translationPath + "standard_service_unit") ||
                "Unidade atendimento padrão"
              }
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormInput
              size="small"
              name="patientEncounterOriginCode"
              fullWidth
              label={
                t(translationPath + "patient_encounter_origin_code") ||
                "PatientEncounterOriginCode"
              }
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormInput
              size="small"
              name="sectorAccommodationType"
              fullWidth
              label={
                t(translationPath + "sector_accommodation_type") ||
                "SectorAccommodationType"
              }
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormInput
              size="small"
              name="patientEncounterPhysicianCode"
              fullWidth
              label={
                t(translationPath + "patient_encounter_physician_code") ||
                "PatientEncounterPhysicianCode"
              }
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormInput
              size="small"
              name="patientEncounterTypeCode"
              fullWidth
              label={
                t(translationPath + "patient_encounter_type_code") ||
                "PatientEncounterTypeCode"
              }
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <FormInput
              size="small"
              name="insuranceAllowCheckin"
              fullWidth
              label={
                t(translationPath + "insurance_allow_checkin") ||
                "InsuranceAllowCheckin"
              }
            />
          </Grid>
        </Grid>
      )}
    />
  );
};

export default SiteAppCheckIn;
